import {
  getCommunity,
  deleteCommunity,
  updateCommunity,
  addCommunity,
} from '@/adapters/master';
import { ICommunity, ICommunityRes, IApiResponse } from '@/interfaces/master';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Flex,
  notification,
  Radio,
  Table,
  TableColumnsType,
  Input,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { uniqueId } from 'lodash';

interface IBatchRejection {
  status: 'rejected' | 'fulfilled';
  reason?: { errors?: { errorMessage?: string } };
}

const getErrorMessage = (error: any): string => {
  if (error && error.message) return error.message;
  return 'เกิดข้อผิดพลาดในการดำเนินการ';
};

const CommunityPage: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<ICommunity[]>([]);
  const [selected, setSelected] = useState<ICommunity[]>([]);
  const [tab, setTab] = useState<'create' | 'update'>('create');
  const [searchText, setSearchText] = useState('');

  const { data, refetch, isFetching } = useQuery<ICommunityRes, Error>({
    queryKey: ['get-community'],
    queryFn: getCommunity,
    retry: 0,
  });

  const notifyBatchErrors = (title: string, res: any, originalData: any[]) => {
    const errors: string[] = [];
    if (Array.isArray(res?.data)) {
      (res.data as IBatchRejection[]).forEach((result, index) => {
        if (result.status === 'rejected') {
          const code = originalData[index]?.SI_SITE || `Index: ${index}`;
          errors.push(
            `${code}: ${result.reason?.errors?.errorMessage || 'Server Error'}`,
          );
        }
      });
    } else if (res?.status !== 'OK')
      errors.push(res?.message || 'Server Error');

    if (errors.length > 0) {
      notification.error({
        message: title,
        style: { width: '500px' },
        description: (
          <ul
            style={{
              paddingLeft: '15px',
              margin: 0,
              maxHeight: '250px',
              overflowY: 'auto',
            }}
          >
            {errors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        ),
      });
      return true;
    }
    return false;
  };

  const actionCommunityMutation = useMutation<
    IApiResponse,
    Error,
    Partial<ICommunity>[]
  >({
    mutationFn: payload =>
      tab === 'create' ? addCommunity(payload) : updateCommunity(payload),
    onSuccess: (res, variables) => {
      if (
        !notifyBatchErrors(
          tab === 'create'
            ? 'Sync Community ไม่สำเร็จ'
            : 'Update Community ไม่สำเร็จ',
          res,
          variables,
        )
      ) {
        notification.success({
          message:
            tab === 'create'
              ? 'Sync Community สำเร็จ'
              : 'Update Community สำเร็จ',
        });
        refetch();
      }
    },
    onError: error =>
      notification.error({
        message: 'เกิดข้อผิดพลาด',
        description: getErrorMessage(error),
      }),
  });

  const actionDeleteCommunity = useMutation<
    IApiResponse,
    Error,
    Partial<ICommunity>[]
  >({
    mutationFn: payload => deleteCommunity(payload),
    onSuccess: (res, variables) => {
      if (!notifyBatchErrors('ลบข้อมูลไม่สำเร็จ', res, variables)) {
        notification.success({ message: 'ลบข้อมูลสำเร็จ' });
        refetch();
      }
    },
  });

  const filteredData = useMemo(() => {
    if (!searchText) return selected;
    const lowerSearch = searchText.toLowerCase();
    return selected.filter(
      item =>
        item.SI_SITE?.toLowerCase().includes(lowerSearch) ||
        item.SI_DESC?.toLowerCase().includes(lowerSearch) ||
        item.PRO_PROVINCE_DESC?.toLowerCase().includes(lowerSearch),
    );
  }, [selected, searchText]);

  const columns: TableColumnsType<ICommunity> = useMemo(
    () => [
      {
        title: 'ลำดับ',
        width: 60,
        align: 'center',
        render: (_, __, i) => <b>{i + 1}</b>,
      },
      {
        title: 'รหัสวิสาหกิจชุมชน',
        dataIndex: 'SI_SITE',
        width: 180,
        sorter: (a, b) => (a.SI_SITE || '').localeCompare(b.SI_SITE || ''),
        filters: Array.from(
          new Set(selected.map(i => i.SI_SITE).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.SI_SITE === value,
        filterSearch: true,
      },
      {
        title: 'ชื่อวิสาหกิจชุมชน',
        dataIndex: 'SI_DESC',
        sorter: (a, b) => (a.SI_DESC || '').localeCompare(b.SI_DESC || ''),
        filters: Array.from(
          new Set(selected.map(i => i.SI_DESC).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.SI_DESC === value,
        filterSearch: true,
      },
      {
        title: 'จังหวัด',
        dataIndex: 'PRO_PROVINCE_DESC',
        width: 180,
        sorter: (a, b) =>
          (a.PRO_PROVINCE_DESC || '').localeCompare(b.PRO_PROVINCE_DESC || ''),
        filters: Array.from(
          new Set(selected.map(i => i.PRO_PROVINCE_DESC).filter(Boolean)),
        )
          .sort()
          .map(v => ({ text: v as string, value: v as string })),
        onFilter: (value, record) => record.PRO_PROVINCE_DESC === value,
        filterSearch: true,
      },
      {
        title: 'จำนวนสมาชิก',
        dataIndex: 'SI_MEMBER_QTY',
        width: 120,
        align: 'right',
        sorter: (a, b) => (a.SI_MEMBER_QTY || 0) - (b.SI_MEMBER_QTY || 0),
        render: val => Number(val || 0).toLocaleString(),
      },
    ],
    [selected],
  );

  useEffect(() => {
    const listMap = {
      create: data?.insert,
      update: data?.update,
      match: data?.match,
    };
    const currentList = listMap[tab] || [];
    setSelected(currentList.map(c => ({ ...c, _key: uniqueId('comm-') })));
    setSelectedRowKeys([]);
    setSelectedRows([]);
  }, [tab, data]);

  const isPending =
    actionCommunityMutation.isPending || actionDeleteCommunity.isPending;

  return (
    <div style={{ padding: '20px', paddingBottom: '50px' }}>
      <Flex justify="space-between" align="center" className="mb-3">
        <Radio.Group
          value={tab}
          onChange={e => setTab(e.target.value)}
          buttonStyle="solid"
        >
          <Radio.Button value="create">เพิ่ม</Radio.Button>
          <Radio.Button value="update">แก้ไข/ลบ</Radio.Button>
        </Radio.Group>
        <Flex gap={12}>
          <Input.Search
            placeholder="ค้นหา (รหัส / ชื่อ / จังหวัด)"
            allowClear
            onSearch={setSearchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 350 }}
          />
          {tab === 'update' && (
            <button
              type="button"
              className="btn btn-outline-danger"
              disabled={!selectedRowKeys.length || isPending}
              onClick={() => actionDeleteCommunity.mutate(selectedRows)}
            >
              ลบข้อมูล Community
            </button>
          )}
          <button
            type="button"
            className="btn btn-success"
            disabled={!selectedRowKeys.length || isPending}
            onClick={() => actionCommunityMutation.mutate(selectedRows)}
          >
            {isPending
              ? 'กำลังส่ง...'
              : tab === 'create'
                ? 'ส่งข้อมูลไป OTOD >'
                : 'แก้ไขข้อมูลที่ OTOD >'}
          </button>
        </Flex>
      </Flex>
      <Table<ICommunity>
        rowSelection={{
          selectedRowKeys,
          onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
          },
        }}
        columns={columns}
        dataSource={filteredData}
        loading={isFetching}
        size="small"
        bordered
        rowKey="_key"
        pagination={false}
        scroll={{ x: 1000 }}
        sticky={{ offsetHeader: 0 }}
      />
    </div>
  );
};

export default CommunityPage;
